import React from 'react';

import { RelativeDateText } from '@plato/ui-legacy/components/common/relative-date-text';
import Spinner from '@plato/ui-legacy/components/ui/spinner';
import { cn } from '@plato/ui/util/classes';

type TimelineListGroupProps = {
    date?: Date;
    title?: string;
    hideWhenEmpty?: boolean;
} & React.PropsWithChildren;

function TimelineListGroup({
    children,
    date,
    title,
    hideWhenEmpty,
}: TimelineListGroupProps): React.ReactNode {
    const itemCounts = React.Children.count(children);

    if (hideWhenEmpty && itemCounts === 0) {
        return null;
    }

    return (
        <div className="flex w-full flex-col">
            <div>
                <div className="text-sm font-medium text-neutral-700 ">
                    {date && <RelativeDateText date={date} />}
                    {title && <span>{title}</span>}
                </div>
            </div>
            <div className="mt-3 flex w-full flex-col">{children}</div>
        </div>
    );
}

export type TimelineListItemProps = {
    icon: React.ElementType;
    onClick?: () => void;
    contentClassName?: string;
} & Omit<React.HTMLAttributes<HTMLElement>, 'onClick' | 'className'> &
    React.PropsWithChildren;

const TimelineListItem = React.forwardRef<HTMLDivElement, TimelineListItemProps>(
    ({ children, contentClassName, icon: Icon, onClick, ...rest }, ref) => {
        const Component = onClick ? 'button' : 'div';

        return (
            <div ref={ref} className="group flex max-w-full flex-row">
                <div className="ml-1 mr-2 flex min-h-9 shrink-0 items-start justify-center pt-1.5">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-neutral-50 text-neutral-700">
                        <Icon className="h-4 w-4" />
                    </div>
                </div>
                <Component
                    {...rest}
                    onClick={onClick}
                    className={cn(
                        'mb-3 flex flex-1 flex-col overflow-hidden rounded-md border border-neutral-100 group-last:mb-0',
                        {
                            'hover:border-neutral-400 text-left cursor-pointer': !!onClick,
                        },
                        contentClassName,
                    )}
                >
                    {children}
                </Component>
            </div>
        );
    },
);

TimelineListItem.displayName = 'TimelineListItem';

type TimelineListItemHeaderProps = {
    className?: string;
} & React.PropsWithChildren;

function TimelineListItemHeader({ children, className }: TimelineListItemHeaderProps): React.ReactNode {
    return (
        <div
            className={cn(
                'flex w-full flex-row justify-between px-3 py-2',
                'border-b border-neutral-100',
                'text-sx',
                'bg-neutral-25 text-neutral-800',
                className,
            )}
        >
            {children}
        </div>
    );
}

type TimelineListItemBodyProps = {
    className?: string;
} & React.PropsWithChildren;

const TimelineListItemBody = React.forwardRef<HTMLDivElement, TimelineListItemBodyProps>(
    ({ children, className }, ref) => {
        return (
            <div ref={ref} className={cn('w-full px-3 py-2', className)}>
                {children}
            </div>
        );
    },
);

TimelineListItemBody.displayName = 'TimelineListItemBody';

export type TimelineListProps = {
    isLoading?: boolean;
    className?: string;
} & React.PropsWithChildren;

export function TimelineList({ children, isLoading, className }: TimelineListProps): React.ReactNode {
    // Hide the timeline list when there are no groups

    const isEmpty =
        React.Children.toArray(children).filter(
            (child) => React.isValidElement(child) && child.type === TimelineListGroup,
        ).length === 0;

    const state = isLoading ? 'loading' : isEmpty ? 'empty' : 'content';

    return (
        <div
            className={cn('flex h-full flex-col gap-3 data-[state=empty]:hidden', className)}
            data-state={state}
        >
            {children}
            {isLoading && (
                <div className="flex items-center self-center">
                    <Spinner />
                </div>
            )}
        </div>
    );
}

TimelineList.Group = TimelineListGroup;
TimelineList.Item = TimelineListItem;
TimelineList.ItemHeader = TimelineListItemHeader;
TimelineList.ItemBody = TimelineListItemBody;
